/*
 * @Author: Liux
 * @Date: 2019-09-26 00:56:47
 * @LastEditor: Liux
 * @LastEditTime: 2021-03-01 09:29:20
 */
import {
  Component,
  HostListener,
  OnInit,
  Input,
  EventEmitter,
  Output,
  forwardRef,
  ViewChild,
  ElementRef,
  TemplateRef,
  Renderer2,
  OnDestroy,
  NgZone,
  ChangeDetectorRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  selector: 'ad-dropdown-list',
  templateUrl: './ad-dropdown-list.component.html',
  styleUrls: ['./ad-dropdown-list.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdDropdownListComponent),
      multi: true,
    },
  ],
  animations: [
    trigger('showAndHidePopup', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scaleY(0.8)' }),
        animate('.12s cubic-bezier(0, 0, 0.2, 1)'),
      ]),
      transition(':leave', [
        animate('.1s linear', style({ opacity: 0, transform: 'scaleY(0.8)' })),
      ]),
    ]),
  ],
})
export class AdDropdownListComponent
  implements OnInit, ControlValueAccessor, OnDestroy
{
  _value: any;
  _dataSource: any[];
  selectedItem;
  mouseDownEventRemover: any;
  @Input() set dataSource(val) {
    this.filterContent = '';
    this._dataSource = [...val];
  }
  get dataSource() {
    return this._dataSource;
  }
  @Input() disabled: boolean = false;
  @Input() cssClass: any;
  @Input() showSearchBox = false;
  @Input() useCheckbox = false;
  @Input() valueField = 'value';
  @Input() textField = 'text';
  @Input() width = 'auto';
  @Input() popupWidth: string;
  @Input() popupAlign = 'right';
  @Input() maxHeight = 260; // only used on the normal scroll model
  @Input() enableVirtualScroll = false;
  @Input() popupHeight = 200; // only used on the virtual scroll model.
  @Input() itemHeight = 35; // only used on the virtual scroll model.
  @Input() multiple = false;
  @Input() defaultText = '';
  @Output() onChange = new EventEmitter();
  @Output() onSelected = new EventEmitter();
  @Output() onUnSelected = new EventEmitter();
  @Output() onOpen = new EventEmitter();
  @Output() onClose = new EventEmitter();
  @Input() displayTemplate: TemplateRef<any>;
  @Input() itemTextTemplate: TemplateRef<any>;
  @Input() checkedIconClass = 'ag-icon ag-icon-checkbox-checked';
  @Input() unCheckedIconClass = 'ag-icon ag-icon-checkbox-unchecked';
  @Input() indeterminateIconClass = 'ag-icon ag-icon-checkbox-indeterminate';
  @Input() selectAllText = 'All Items';
  @Input() showSelectAll = true;
  @Input() textAlign: 'left' | 'right' = 'right';
  @Input() arrowIconToLeft = false;
  // if true when the user changes the selectedItem by the NgModel outside that will emit the change event.
  // NOTE:Currently this is not worked on the multiple selection mode.
  @Input() isEmitChangeOutside: boolean = true;
  @Input() multipleSelectedDisplay = (selectedItem: any[], allItems: any[]) => {
    if (selectedItem.length < allItems.length) {
      return `${selectedItem.length} options selected`;
    } else {
      return `All options selected`;
    }
  };
  @Input() filter: (item, filterContent) => boolean;

  popupStyle: any;
  filterContent = '';
  get dataSourceAfterFiltered() {
    if (
      (this.filterContent !== undefined &&
        this.filterContent !== null &&
        this.filterContent.length > 0) ||
      this.filter
    ) {
      return this.dataSource.filter((item) => {
        if (this.filter === undefined || this.filter === null) {
          if (
            item[this.textField] !== undefined &&
            item[this.textField] !== null &&
            item[this.textField]
              .toLowerCase()
              .indexOf(this.filterContent.toLowerCase()) > -1
          ) {
            return true;
          }
          return false;
        } else {
          return this.filter(item, this.filterContent);
        }
      });
    }
    return this.dataSource;
  }

  get displayTemplateContext() {
    return {
      value: this.value,
      valueField: this.valueField,
      textField: this.textField,
      multiple: this.multiple,
      dataSource: this.dataSource,
      defaultText: this.defaultText,
    };
  }

  get selectAllClass() {
    if (this.multiple) {
      const status = this.selectAllStatus;
      if (status === 0) {
        return this.unCheckedIconClass + ' dd-list-uncheck';
      } else if (status === 1) {
        return this.checkedIconClass + ' dd-list-check';
      } else {
        return this.indeterminateIconClass + ' dd-list-indeterminate';
      }
    }
  }

  get selectAllStatus() {
    if (this.value.length === 0 || this.dataSourceAfterFiltered.length === 0) {
      return 0;
    }
    let allIsSelected = true;
    let allIsNotSelected = true;
    this.dataSourceAfterFiltered.forEach((item) => {
      const selectedItem = this.value.find(
        (p) => p[this.valueField] === item[this.valueField]
      );
      if (selectedItem === undefined) {
        if (allIsSelected) {
          allIsSelected = false;
        }
      } else {
        if (selectedItem.selected && allIsNotSelected) {
          allIsNotSelected = false;
        }
        if (!selectedItem.selected && allIsSelected) {
          allIsSelected = false;
        }
      }
    });
    if (allIsNotSelected) {
      return 0;
    }
    if (allIsSelected) {
      return 1;
    }
    return -1;
  }

  @ViewChild('container', { static: true }) containerElementRef: ElementRef;
  @ViewChild('ddListBox', { static: true }) ddListBox: ElementRef;

  get listBoxHeight() {
    return this.ddListBox.nativeElement.offsetHeight;
  }

  constructor(
    private renderer: Renderer2,
    private ngZone: NgZone,
    private cd: ChangeDetectorRef
  ) {}
  ngOnDestroy(): void {
    this.mouseDownEventRemover();
  }

  ngOnInit() {
    this.ngZone.runOutsideAngular(() => {
      this.mouseDownEventRemover = this.renderer.listen(
        'document',
        'mousedown',
        this.mouseDown.bind(this)
      );
    });

    if (this.dataSource) {
      const result = this.dataSource.filter((item) => item.selected);
      if (result.length > 0) {
        this.selectOptions(result);
      }
    }
    this.popupWidth || 'auto';
    // if (isNumber(pwidth)) {
    //   //this.popupWidth = pwidth + 'px';
    //   // this.popupStyle = {
    //   //   width: pwidth + 'px',
    //   //   height: 'auto',
    //   // };
    // } else {
    //   //this.popupWidth = 'auto';
    //   // this.popupStyle = {
    //   //   width: pwidth,
    //   //   height: 'auto',
    //   // };
    // }
  }

  getPopupWidth() {
    let w = this.popupWidth || 'auto';
    if (!isNaN(+w)) {
      return w + 'px';
    } else {
      return w;
    }
  }

  trackByFunFactory() {
    return (item: any) => item[this.valueField];
  }
  selectAllClick(event) {
    const status = this.selectAllStatus;
    if (status === 1) {
      this.unselectOptions(this.dataSourceAfterFiltered);
    } else if (status === -1) {
      this.selectOptions(
        this.dataSourceAfterFiltered.filter((item) => !item.selected)
      );
    } else {
      this.selectOptions(this.dataSourceAfterFiltered);
    }
    this.onSelected.emit({ selectedValue: this.value, target: this });
    event.stopPropagation();
  }
  public unselectOptions(items: any[], emitEvent = true): boolean {
    const unSelectedArray = [];
    if (this.multiple) {
      items.forEach((item) => {
        const mappedItem = this.dataSource.find(
          (ditem) => ditem[this.valueField] === item[this.valueField]
        );
        if (mappedItem) {
          unSelectedArray.push(mappedItem);
          this.changeDatasourceSelectState(item, false);
        }
      });
      this.valueOnChange(this.value);

      // const ids = items.map((item) => {
      //   return item[this.valueField];
      // });
      // const mappedItems = this.dataSource.filter(
      //   (item) => ids.indexOf(item[this.valueField]) !== -1
      // );
      // const newValue = this.value.filter(
      //   (item) => ids.indexOf(item[this.valueField]) === -1
      // );
      // mappedItems.forEach((item) => {
      //   this.changeDatasourceSelectState(item, false);
      // });

      if (emitEvent) {
        this.onUnSelected.emit({
          value: this.value,
          target: this,
          unSelectedArray,
        });
        this.onChange.emit(this.value);
      }
    }

    return true;
  }
  public unselectOption(item, emitEvent = true) {
    if (this.multiple) {
      this.changeDatasourceSelectState(item, false);
      this.valueOnChange(this.value);
      if (emitEvent) {
        this.onUnSelected.emit({
          value: this.value,
          target: this,
          unSelectedArray: [item],
        });
      }
      if (emitEvent) {
        this.onChange.emit(this.value);
      }
    }
  }

  // 'emitCustomEvent'
  public selectOption(item, emitCustomEvent = true) {
    if (this.multiple) {
      // if (!this.value || !isArray(this.value)) {
      //   this._value = [];
      // }
      if (this.checkItemIsInDataSource(item)) {
        this.changeDatasourceSelectState(item, true);
        // this.value.push(item);
        if (emitCustomEvent) {
          this.valueOnChange(this.value);
        }

        if (emitCustomEvent) {
          this.onChange.emit(this.value);
        }
      }
    } else {
      if (this.checkItemIsInDataSource(item)) {
        if (this.value !== item[this.valueField]) {
          this.value = item[this.valueField];
          if (emitCustomEvent) {
            this.onChange.emit(item);
          }
        }
      }
    }
  }

  public selectOptions(items, emitCustomEvent = true) {
    if (items && Array.isArray(items) && items.length > 0) {
      if (this.multiple) {
        items.forEach((item) => this.selectOption(item, false));
        this.valueOnChange(this.value);
        this.onChange.emit(this.value);
      } else {
        this.selectOption(items[0], emitCustomEvent);
      }
    }
  }

  changeDatasourceSelectState(item, isSelect) {
    const mappedItem = this.dataSource.find(
      (ditem) => ditem[this.valueField] === item[this.valueField]
    );
    if (mappedItem) {
      mappedItem.selected = isSelect;
    }
  }

  get displayName() {
    if (this.value !== undefined || Number.isInteger(this.value)) {
      if (this.multiple) {
        if (this.value.length === 0) {
          return this.defaultText;
        } else if (this.value.length === 1) {
          return this.value[0][this.textField];
        } else {
          if (
            this.multipleSelectedDisplay &&
            typeof this.multipleSelectedDisplay === 'function'
          ) {
            return this.multipleSelectedDisplay(this.value, this.dataSource);
          } else {
            return '' + this.value.length + ' options selected';
          }
        }
      } else {
        const result = this.dataSource.find(
          (item) => item[this.valueField] === this.value
        );
        if (result) {
          return result[this.textField];
        } else {
          return this.defaultText;
        }
      }
    }
    return this.defaultText;
  }

  get value() {
    if (this.multiple) {
      return this.dataSource.filter((item) => item.selected);
    } else {
      return this._value;
    }
  }

  set value(val) {
    if (this.multiple) {
      val.forEach((item) => {
        const matchedItem = this.dataSource.find(
          (ditem) => ditem[this.valueField] === item[this.valueField]
        );
        matchedItem.selected = true;
      });
      this.valueOnChange(this.value);
    } else {
      this._value = val;
      this.valueOnChange(val);
    }
  }

  _isOpen = false;
  set isOpen(value) {
    this._isOpen = value;
    if (value) {
      this.onOpen.emit();
    } else {
      this.onClose.emit();
    }
  }

  get isOpen() {
    return this._isOpen;
  }
  onClick(event) {
    const clickEvent = document.createEvent('MouseEvents');
    clickEvent.initEvent('mousedown', true, true);
    (clickEvent as any).realTarget = this.containerElementRef.nativeElement;
    document.dispatchEvent(clickEvent); // trigger document click event to close other dropdown list popup window.
    this.isOpen = !this.isOpen;
    event.stopPropagation();
  }

  itemClick(item, event) {
    const result = this.dataSource.find(
      (sitem) => sitem[this.valueField] === item[this.valueField]
    );
    let currentItem;
    if (result) {
      currentItem = result;
    }
    if (this.multiple) {
      if (currentItem.selected) {
        this.unselectOption(currentItem);
      } else {
        this.selectOption(currentItem);
        this.onSelected.emit({ selectedValue: currentItem, target: this });
      }
    } else {
      this.selectOption(currentItem);
      this.onSelected.emit({ selectedValue: currentItem, target: this });
      this.isOpen = false;
    }
    event.stopPropagation();
    return false;
  }

  //#region ngModel
  valueOnChange = (v: any) => {};
  valueOnTouched = () => {};
  writeValue(obj: any): void {
    if (this.dataSource && obj) {
      if (this.multiple) {
        this.selectOptions(obj);
        if (this.isEmitChangeOutside) {
          this.onChange.emit(this.value);
        }
      } else {
        const result = this.dataSource.filter(
          (item) => item[this.valueField] === obj
        );
        if (result.length > 0) {
          this.selectOptions(result, this.isEmitChangeOutside);
          if (this.isEmitChangeOutside) {
            this.onChange.emit(this.value);
          }
        } else {
          this._value = obj;
        }
      }
    } else {
      this._value = obj;
      if (this.isEmitChangeOutside) {
        this.onChange.emit(this.value);
      }
    }
  }
  registerOnChange(fn: any): void {
    this.valueOnChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.valueOnTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    // throw new Error("Method not implemented.");
  }
  //#endregion

  checkItemIsInDataSource(item) {
    const result = this.dataSource.findIndex(
      (dsitem) => dsitem[this.valueField] === item[this.valueField]
    );
    return result === -1 ? false : true;
  }

  @HostListener('mousedown', ['$event'])
  selfMouseDown(e: Event) {
    e.stopPropagation();
  }

  // @HostListener('document:mousedown', ['$event'])
  mouseDown(e: Event): void {
    if (
      this.isOpen &&
      !this.containerElementRef.nativeElement.contains(e.target) &&
      (!(e as any).realTarget ||
        this.containerElementRef.nativeElement !== (e as any).realTarget)
    ) {
      this.ngZone.run(() => {
        this.isOpen = false;
      });

      //this.cd.detectChanges();
    }
  }

  closePopup() {
    this.isOpen = false;
  }

  inputSearch(e: Event): void {
    e.preventDefault();
    e.stopPropagation();
  }
}
